import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useState } from 'react';

const POSTHOG_HOST = import.meta.env.REACT_APP_POSTHOG_HOST;
const POSTHOG_KEY = import.meta.env.REACT_APP_POSTHOG_KEY;

const isEnabled = !!POSTHOG_HOST && !!POSTHOG_KEY;

export type FeatureFlagValue = boolean | string | undefined;

export type FeatureFlags = Record<string, FeatureFlagValue>;

export interface FeatureFlagsResult {
  flags: FeatureFlags;
  ready: boolean;
}

export const useFeatureFlags = (): FeatureFlagsResult => {
  const ph = usePostHog();
  const [flags, setFlags] = useState<FeatureFlags>({});
  // Without PostHog there are no flags to wait for.
  const [ready, setReady] = useState(!isEnabled);

  const computeFlags = useCallback(() => {
    if (!isEnabled || !ph) {
      return {};
    }

    const allFlags = ph.featureFlags.getFlagVariants() || {};
    const result: FeatureFlags = {};

    for (const [key, value] of Object.entries(allFlags)) {
      if (typeof value === 'boolean') {
        result[key] = value;
      } else if (typeof value === 'string') {
        result[key] =
          value === 'true' ? true : value === 'false' ? false : value;
      }
    }

    return result;
  }, [ph]);

  useEffect(() => {
    if (!isEnabled || !ph) {
      if (!isEnabled) {
        console.warn(
          '[useFeatureFlags] PostHog is not configured. Feature flags will return empty.',
        );
      }
      return;
    }

    setFlags(computeFlags());

    return ph.onFeatureFlags(() => {
      setFlags(computeFlags());
      setReady(true);
    });
  }, [ph, computeFlags]);

  return { flags, ready };
};

export const useFeatureFlag = (key: string): FeatureFlagValue => {
  const ph = usePostHog();

  if (!isEnabled || !ph) {
    return undefined;
  }

  return ph.getFeatureFlag(key) as FeatureFlagValue;
};
